import React, { useRef, useState, useEffect } from 'react';
import Sortable from 'sortablejs';
import cn from 'classnames';

const sortableStoresMap = new Map();
const disabledClassName = 'sortable-disabled';

export default function ({
  children,
  className,
  options,
  onDragStart = () => {},
  onDragEnd = () => {},
  dragOnly = false,
  enabled = true,
  showDecor = false,
  container = null,
  draggableSelector = '.all-overview-icons__icon, .all-overview-icons__connected',
  nodes = [],
  ...rest
}) {
  const sortableRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sortable, setSortable] = useState(null);

  const handleStart = (e) => {
    setIsDragging(true);
    if (container && container.current) {
      // using classList instead of React className, because changing className
      // causes DOM to re-render and updates sortable list.

      container.current.classList.add(disabledClassName);
    }
    onDragStart(e, nodes);
  };

  const handleEnd = (e) => {
    setIsDragging(false);
    if (container && container.current) {
      container.current.classList.remove(disabledClassName);
    }
    // Adjust the newDraggableIndex if it's out of bounds to fix the issue on FY-38763
    const adjustedNewIndex = Math.min(e.newDraggableIndex, nodes.length - 1);
    // Pass the adjusted index to onDragEnd
    onDragEnd({ ...e, newDraggableIndex: adjustedNewIndex }, nodes);
  };

  const getInstanceId = (data) =>
    Object.keys(data).find((key) => key.indexOf('Sortable') > -1);

  const handleMove = (e) => {
    // get Sortable instance ID for both elements. Ex: Sortable1619623893842 (based on time)
    const droppingZoneInstanceId = getInstanceId(e.to);
    const draggingElementInstanceId = getInstanceId(e.from);
    // get options from that instance
    const { group: toGroup } =
      (e && e.to[droppingZoneInstanceId].options) || [];
    const { group: fromGroup } =
      (e && e.from[draggingElementInstanceId].options) || [];
    if (toGroup && fromGroup) {
      // return false if the group doesn't match, that means we cancel the drop.
      // return true if the group matches, proceed with the reorder.
      return toGroup.name === fromGroup.name;
    }
    return false;
  };

  useEffect(() => {
    if (sortable) {
      sortable.option('onEnd', handleEnd);
      sortable.option('onStart', handleStart);
    }
  }, [nodes, sortable]);

  const sortableOptions = {
    animation: 150,
    draggable: draggableSelector,
    delay: 200,
    delayOnTouchOnly: true,
    scrollSensitivity: 50,
    swapThreshold: 0.8,
    forceFallback: true,
    fallbackTolerance: 5,
    revertOnSpill: true,
    sort: !dragOnly,
    ...options,
    onStart: handleStart,
    onEnd: handleEnd,
    onMove: handleMove,
  };

  useEffect(() => {
    if (!enabled) return;
    let sortable;
    if (sortableRef && sortableRef.current) {
      sortable = Sortable.create(sortableRef.current, sortableOptions);
      setSortable(sortable);
    }
    return () => {
      if (sortableRef && sortableRef.current) {
        sortableStoresMap.delete(sortableRef.current);
      }
      setSortable(sortable);
      sortable && sortable.destroy();
    };
  }, [enabled, dragOnly]);

  return (
    <div
      ref={sortableRef}
      role="tablist"
      tabIndex={-1}
      className={cn(className, {
        dragging: isDragging,
      })}
      {...rest}
    >
      {children}
      {showDecor && <div className="sortable-decor" />}
    </div>
  );
}
